import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { Table, Select, InputNumber, Button, Tag, Input, Spin, Tooltip as AntTooltip, Drawer, List, Badge, message, Modal, Checkbox, Pagination } from 'antd'
import adminRequest from 'utils/adminRequest'
import {
  TeamOutlined, TrophyOutlined, BarChartOutlined,
  WomanOutlined, BookOutlined, SearchOutlined, RiseOutlined,
  BankOutlined, CheckCircleOutlined, CloseCircleOutlined, FileSearchOutlined,
  UserOutlined, ApartmentOutlined, LinkOutlined, CopyOutlined, UsergroupAddOutlined,
  FilterOutlined, InboxOutlined,
} from '@ant-design/icons'
import {
  ComposedChart, BarChart, Bar, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts'
import styled from 'styled-components'

const { Option } = Select

const toArr = v => Array.isArray(v) ? v : (v != null && v !== '' ? [v] : [])

const GENDER_OPTIONS = [
  { value: 'MALE', label: 'Male' },
  { value: 'FEMALE', label: 'Female' },
  { value: 'OTHER', label: 'Other' },
]

const FILTER_CATS = [
  { key: 'company',    label: 'Company' },
  { key: 'degree',     label: 'Degree' },
  { key: 'dept',       label: 'Department' },
  { key: 'state',      label: 'State' },
  { key: 'gender',     label: 'Gender' },
  { key: 'minMarks',   label: 'Min Marks %' },
  { key: 'maxArrears', label: 'Max Arrears' },
]

const EMPTY_PENDING = { company: undefined, degree: [], dept: [], state: [], gender: [], minMarks: undefined, maxArrears: undefined }

// Sentinel used to neutralise the shortlist's default arrears ceiling (the API
// applies maxArrears=2 when the field is omitted, so we must send an explicit
// high value rather than dropping it). Must stay within the API's accepted
// range — the schema caps maxArrears at 50, and a previous value of 999 made
// every corporate meta-dashboard call fail with
// "body/maxArrears must be <= 50". No candidate has >50 active arrears, so 50
// is effectively "no ceiling".
const NO_ARREARS_CEILING = 50

const SummaryGrid = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: 8px;
  padding: 0 0 12px;
  margin-bottom: 8px;
`

const KPICard = styled.div`
  background: #ffffff;
  border: 1px solid ${p => p.$active ? (p.$color || '#3249D7') : '#e8ecf4'};
  border-radius: 10px;
  padding: 10px 14px;
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1 1 0;
  min-width: 0;
  box-shadow: 0 1px 4px rgba(0,0,0,0.05);
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s;
  cursor: ${p => p.$clickable ? 'pointer' : 'default'};
  &:hover {
    transform: ${p => p.$clickable ? 'translateY(-2px)' : 'none'};
    box-shadow: 0 4px 16px rgba(50,73,215,0.10);
    border-color: ${p => p.$color || '#3249D7'};
  }
`

const IconWrap = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: ${p => (p.color || '#3249D7')}15;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 17px;
  color: ${p => p.color || '#3249D7'};
  flex-shrink: 0;
`

const KPIValue = styled.div`
  font-size: 19px;
  font-weight: 800;
  color: #1a1a2e;
  line-height: 1.1;
  white-space: nowrap;
`

const KPILabel = styled.div`
  font-size: 11px;
  color: #7b8499;
  margin-top: 2px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 20px 0 12px;
`

const SectionTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #1a1a2e;
`

const SectionLink = styled.span`
  font-size: 13px;
  color: #3249D7;
  cursor: pointer;
  font-weight: 500;
  &:hover { text-decoration: underline; }
`

const StatCardsRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 4px;
`

const StatCard = styled.div`
  background: #ffffff;
  border: 1px solid #e8ecf4;
  border-left: 4px solid ${p => p.$accent || '#3249D7'};
  border-radius: 10px;
  padding: 14px 18px;
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 140px;
  flex: 1;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04);
`

const StatIcon = styled.div`
  font-size: 22px;
  color: ${p => p.$color || '#3249D7'};
`

const StatValue = styled.div`
  font-size: 24px;
  font-weight: 800;
  color: #1a1a2e;
  line-height: 1;
`

const StatLabel = styled.div`
  font-size: 11px;
  color: #7b8499;
  margin-top: 3px;
  font-weight: 500;
`

const FilterPopup = styled.div`
  display: flex;
  height: 360px;
  overflow: hidden;
`
const FilterSidebar = styled.div`
  width: 140px;
  border-right: 1px solid #f0f0f0;
  overflow-y: auto;
  flex-shrink: 0;
`
const FilterSidebarItem = styled.div`
  padding: 9px 14px;
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${p => p.$active ? '#f0f4ff' : 'transparent'};
  color: ${p => p.$active ? '#3249D7' : '#333'};
  font-weight: ${p => p.$active ? 600 : 400};
  border-right: ${p => p.$active ? '2px solid #3249D7' : '2px solid transparent'};
  &:hover { background: #f5f7ff; }
`
const FilterPanel = styled.div`
  flex: 1;
  padding: 12px 16px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
`
const FilterChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 10px;
  min-height: 0;
`

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin: 20px 0 28px;
  @media (max-width: 768px) { grid-template-columns: 1fr; }
`

const ChartCard = styled.div`
  background: #ffffff;
  border: 1px solid #e8ecf4;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04);
`

const ChartTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0 0 12px;
`

const PlacementGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 24px;
`

const PlacementCard = styled.div`
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border: 1px solid rgba(255, 255, 255, 0.75);
  border-radius: 12px;
  padding: 10px 14px;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  box-shadow: 0 4px 24px rgba(50, 73, 215, 0.08), inset 0 1px 0 rgba(255,255,255,0.9);
  transition: transform 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
  border-left: 4px solid ${p => p.accent || '#3249D7'};
  flex: 0 0 auto;
  white-space: nowrap;
  cursor: ${p => p.$clickable ? 'pointer' : 'default'};
  ${p => p.$active && `
    background: rgba(50, 73, 215, 0.07);
    box-shadow: 0 4px 24px rgba(50, 73, 215, 0.18), inset 0 1px 0 rgba(255,255,255,0.9);
  `}
  &:hover {
    transform: ${p => p.$clickable ? 'translateY(-2px)' : 'none'};
    box-shadow: ${p => p.$clickable ? '0 8px 28px rgba(50, 73, 215, 0.13), inset 0 1px 0 rgba(255,255,255,0.9)' : '0 4px 24px rgba(50, 73, 215, 0.08), inset 0 1px 0 rgba(255,255,255,0.9)'};
  }
`

const PlacementCardIcon = styled.div`
  font-size: 18px;
  color: ${p => p.color || '#3249D7'};
  flex-shrink: 0;
  line-height: 1;
`

const PlacementCardValue = styled.div`
  font-size: 20px;
  font-weight: 800;
  color: ${p => p.color || '#1a1a2e'};
  line-height: 1;
  flex-shrink: 0;
`

const PlacementCardLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  line-height: 1.2;
`


const copyLink = (link, name) => {
  navigator.clipboard.writeText(link).then(() => {
    message.success(`Apply link copied${name ? ` for ${name}` : ''}`, 2)
  }).catch(() => {
    message.error('Failed to copy link', 2)
  })
}

const copyAllLinks = (students) => {
  const lines = students.map(s => `${s.full_name || 'Student'}: ${s.apply_link}`)
  navigator.clipboard.writeText(lines.join('\n')).then(() => {
    message.success(`${students.length} apply link${students.length !== 1 ? 's' : ''} copied to clipboard`, 2)
  }).catch(() => {
    message.error('Failed to copy links', 2)
  })
}

const GENDER_COLORS = ['#3249D7', '#f5222d', '#faad14']
const MARKS_COLORS = ['#f5222d', '#faad14', '#fadb14', '#52c41a', '#1890ff']

const noWrapHeader = () => ({ style: { whiteSpace: 'nowrap' } })

// Server-side sort tokens for the campus-quality table — the FE column keys
// don't all match the API's sortBy contract, so map them. Shortlist and
// companies column keys already match their API tokens, so they send the
// column key directly.
const CAMPUS_SORT_FIELD = {
  campus:   'campusName',
  inst:     'instituteName',
  state:    'state',
  students: 'students',
  marks:    'marks',
  arr:      'zero_arrears_pct',
  fem:      'female_pct',
  score:    'score',
}

// AntD sort order ('ascend'/'descend') → API sortOrder ('asc'/'desc').
const toApiOrder = order => (order === 'ascend' ? 'asc' : 'desc')

// Department Breakdown table is fully server-side now: each column maps to
// the `sortBy` token the API understands. The department-name column uses
// the `departmentName` alias from the API contract; the rest map to their
// SQL aggregate aliases. Columns are built inside the component so their
// `sortOrder` can be driven by the controlled deptSort state.
const DEPT_SORT_FIELD = {
  dept:   'departmentName',
  count:  'candidate_count',
  marks:  'avg_marks',
  hp:     'high_performers',
  ca:     'clear_arrears',
  cp:     'clear_pct',
  female: 'female_count',
}


const CorporateTab = ({
  corporateSummary,
  corporateGlobalSummary,
  corporateDepartments,
  corporateDepartmentsTable,
  corporateDepartmentsTableLoading,
  corporateShortlist,
  corporateShortlistTable,
  corporateShortlistTableLoading,
  corporateCampusQualityTable,
  corporateCampusQualityTableLoading,
  corporatePlacementSummary,
  corporateCompanies,
  corporateCompaniesLoading,
  filterDepartments,
  filterStates,
  filterDegrees,
  fetchCorporateData,
  fetchCorporateDepartmentsTable,
  fetchCorporateShortlistTable,
  fetchCorporateCampusQualityTable,
  fetchCorporatePlacementSummary,
  fetchCorporateCompanies,
}) => {
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_PENDING)
  const [pendingFilters, setPendingFilters] = useState(EMPTY_PENDING)
  const [filterOpen, setFilterOpen] = useState(false)
  const [activeCat, setActiveCat] = useState('company')
  const [catSearch, setCatSearch] = useState('')
  const [searchText, setSearchText] = useState('')                   // shortlist search input
  const [shortlistSearchQuery, setShortlistSearchQuery] = useState('') // debounced, drives the API
  const [campusQualitySearch, setCampusQualitySearch] = useState('')   // campus search input
  const [campusSearchQuery, setCampusSearchQuery] = useState('')       // debounced, drives the API
  const [deptSearch, setDeptSearch] = useState('')          // controlled input value
  const [deptSearchQuery, setDeptSearchQuery] = useState('') // debounced value that drives the API
  const [deptPage, setDeptPage] = useState(1)
  const [deptPageSize, setDeptPageSize] = useState(10)
  const [deptSort, setDeptSort] = useState({ field: undefined, order: undefined })
  const [companiesSort, setCompaniesSort] = useState({ columnKey: undefined, order: undefined })
  const [companiesPage, setCompaniesPage] = useState(1)
  const [companiesPageSize, setCompaniesPageSize] = useState(10)
  const [companiesSearch, setCompaniesSearch] = useState('')           // companies search input
  const [companiesSearchQuery, setCompaniesSearchQuery] = useState('') // debounced, drives the API
  const companiesSectionRef = useRef(null)
  const shortlistRef = useRef(null)
  const [shortlistPage, setShortlistPage] = useState(1)
  const [shortlistPageSize, setShortlistPageSize] = useState(10)
  const [shortlistSort, setShortlistSort] = useState({ field: undefined, order: undefined })
  const [campusPage, setCampusPage] = useState(1)
  const [campusPageSize, setCampusPageSize] = useState(10)
  const [campusSort, setCampusSort] = useState({ field: undefined, order: undefined })
  const [jobRolesDrawer, setJobRolesDrawer] = useState({ open: false, companyName: '', data: [], loading: false })
  const [studentsDrawer, setStudentsDrawer] = useState({ open: false, companyName: '', type: 'applied', data: [], loading: false })
  const [potentialDrawer, setPotentialDrawer] = useState({ open: false, roleTitle: '', role: null, students: [], campuses: [], total_eligible: 0, loading: false })
  const [roleCandidatesDrawer, setRoleCandidatesDrawer] = useState({ open: false, roleTitle: '', type: 'applied', data: [], loading: false })
  const [companyDrawer, setCompanyDrawer] = useState({ open: false, company: null, jobRoles: [], loading: false })

  // Role Applied Candidate table — self-contained, fetched directly via
  // adminRequest into local state (mirrors the allCompanies snapshot pattern),
  // so it needs no new Redux action/reducer/connect wiring.
  const [raRoles, setRaRoles] = useState([])               // dropdown options (server-filtered by raRoleSearch)
  const [raRolesLoading, setRaRolesLoading] = useState(false)
  const [raRoleIds, setRaRoleIds] = useState([])           // multi-select role filter (empty = all roles)
  const [raRoleSearch, setRaRoleSearch] = useState('')     // raw input typed inside the roles dropdown
  const [raRoleSearchQ, setRaRoleSearchQ] = useState('')   // debounced; drives the roles API
  const [raSearch, setRaSearch] = useState('')             // student search input
  const [raSearchQuery, setRaSearchQuery] = useState('')   // debounced, drives the API
  const [raData, setRaData] = useState({ data: [], total: 0, page: 1, limit: 10 })
  const [raLoading, setRaLoading] = useState(false)
  const [raPage, setRaPage] = useState(1)
  const [raPageSize, setRaPageSize] = useState(10)

  // Full company list, kept separate from the Redux corporateCompanies
  // slice that the table renders. The table search bar replaces
  // corporateCompanies with a server-side narrowed result, but the
  // Company filter in the popup must keep showing all companies regardless.
  const [allCompanies, setAllCompanies] = useState([])

  useEffect(() => {
    fetchCorporateData()
    fetchCorporatePlacementSummary()
    // Companies, shortlist & campus-quality tables fetch themselves through
    // their own server-side effects below.
    // Snapshot the unfiltered company list once on mount for the
    // Company filter dropdown. Bigger limit so growing past the default
    // 100 still surfaces everything in the dropdown.
    adminRequest.post('/meta-dashboard/corporate/companies', { limit: 500 })
      .then(res => {
        const payload = res?.data?.data
        const rows = Array.isArray(payload) ? payload : (payload?.data || [])
        setAllCompanies(rows)
      })
      .catch(() => { /* dropdown will fall back to corporateCompanies */ })
  }, [])

  // Debounce the in-dropdown search so we don't fire one request per
  // keystroke. 300 ms feels responsive without spamming the API.
  useEffect(() => {
    const t = setTimeout(() => setRaRoleSearchQ(raRoleSearch.trim()), 300)
    return () => clearTimeout(t)
  }, [raRoleSearch])

  // Role Applied Candidate — server-side filtered roles dropdown. Reloads
  // whenever the debounced search changes; backend caps at 200 rows.
  useEffect(() => {
    let cancelled = false
    setRaRolesLoading(true)
    adminRequest.get('/meta-dashboard/corporate/role-applied/roles', {
      params: raRoleSearchQ ? { search: raRoleSearchQ } : {},
    })
      .then(res => {
        if (cancelled) return
        const payload = res?.data?.data
        setRaRoles(Array.isArray(payload) ? payload : (payload?.data || []))
      })
      .catch(() => { if (!cancelled) setRaRoles([]) })
      .finally(() => { if (!cancelled) setRaRolesLoading(false) })
    return () => { cancelled = true }
  }, [raRoleSearchQ])

  // Debounce the student search box so we don't hit the API on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setRaSearchQuery(prev => {
        const next = raSearch.trim()
        if (next !== prev) setRaPage(1) // new search → back to page 1
        return next
      })
    }, 400)
    return () => clearTimeout(t)
  }, [raSearch])

  // Fetch the candidate rows whenever the role filter, debounced search or
  // pagination changes. `cancelled` guards against out-of-order responses.
  useEffect(() => {
    let cancelled = false
    setRaLoading(true)
    adminRequest.post('/meta-dashboard/corporate/role-applied/candidates', {
      roleIds: raRoleIds.length > 0 ? raRoleIds : undefined,
      search:  raSearchQuery || undefined,
      page:    raPage,
      limit:   raPageSize,
    })
      .then(res => {
        if (cancelled) return
        const payload = res?.data?.data
        setRaData({
          data:  Array.isArray(payload) ? payload : (payload?.data || []),
          total: payload?.total ?? payload?.count ?? 0,
          page:  payload?.page || raPage,
          limit: payload?.limit || raPageSize,
        })
      })
      .catch(() => { if (!cancelled) setRaData({ data: [], total: 0, page: 1, limit: raPageSize }) })
      .finally(() => { if (!cancelled) setRaLoading(false) })
    return () => { cancelled = true }
  }, [raRoleIds, raSearchQuery, raPage, raPageSize])


  const buildPayload = (f) => {
    const p = {}
    if (f.company)                     p.company    = f.company
    if (toArr(f.degree).length)        p.degree     = toArr(f.degree)
    if (toArr(f.dept).length)          p.dept       = toArr(f.dept)
    if (toArr(f.state).length)         p.state      = toArr(f.state)
    if (toArr(f.gender).length)        p.gender     = toArr(f.gender)
    if (f.minMarks > 0)                p.minMarks   = f.minMarks
    if (f.maxArrears != null)          p.maxArrears = f.maxArrears
    return p
  }

  // ── Department Breakdown — server-side pagination / search / sort ──
  // Debounce the search box: when the typed value settles, commit it to
  // deptSearchQuery (which the fetch effect depends on) and reset to page 1.
  useEffect(() => {
    const t = setTimeout(() => {
      setDeptSearchQuery(prev => {
        const next = deptSearch.trim()
        if (next !== prev) setDeptPage(1) // a new search always starts at page 1
        return next
      })
    }, 400)
    return () => clearTimeout(t)
  }, [deptSearch])

  // Single source of truth for the table: every page / page-size / search /
  // sort / applied-filter change refetches from the server. No client-side
  // slicing, filtering or sorting happens anywhere below.
  useEffect(() => {
    fetchCorporateDepartmentsTable({
      ...buildPayload(appliedFilters),
      page: deptPage,
      limit: deptPageSize,
      search: deptSearchQuery || undefined,
      sortBy: deptSort.field,
      sortOrder: deptSort.order === 'ascend' ? 'asc' : 'desc',
    })
  }, [appliedFilters, deptPage, deptPageSize, deptSearchQuery, deptSort.field, deptSort.order])

  // AntD fires onChange for both pagination and sorting; branch on the
  // action so each kind of change maps to the right state update.
  const handleDeptTableChange = (pagination, _filters, sorter, extra) => {
    if (extra?.action === 'sort') {
      const field = sorter?.order ? (DEPT_SORT_FIELD[sorter.columnKey] || undefined) : undefined
      const order = sorter?.order || undefined
      setDeptSort({ field, order })
      setDeptPage(1) // a fresh sort always starts at page 1
    } else if (extra?.action === 'paginate') {
      if (pagination.pageSize !== deptPageSize) {
        setDeptPageSize(pagination.pageSize)
        setDeptPage(1) // page-size change resets to page 1
      } else {
        setDeptPage(pagination.current)
      }
    }
  }

  const deptColumns = useMemo(() => {
    const sortOrderFor = key => (deptSort.field === DEPT_SORT_FIELD[key] ? deptSort.order : null)
    return [
      { title: 'Department', dataIndex: 'department', key: 'dept', ellipsis: true, render: v => <strong>{v}</strong>, sorter: true, sortOrder: sortOrderFor('dept') },
      { title: 'Candidates', dataIndex: 'candidate_count', key: 'count', width: 100, sorter: true, sortOrder: sortOrderFor('count') },
      { title: 'Avg Marks', dataIndex: 'avg_marks', key: 'marks', width: 100, render: v => v != null ? `${parseFloat(v).toFixed(1)}%` : '—', sorter: true, sortOrder: sortOrderFor('marks') },
      { title: 'High Performers', dataIndex: 'high_performers', key: 'hp', width: 130, sorter: true, sortOrder: sortOrderFor('hp') },
      { title: 'No Arrears', dataIndex: 'clear_arrears', key: 'ca', width: 100, sorter: true, sortOrder: sortOrderFor('ca') },
      { title: 'Clear %', dataIndex: 'clear_pct', key: 'cp', width: 90, render: v => v != null ? `${parseFloat(v).toFixed(1)}%` : '—', sorter: true, sortOrder: sortOrderFor('cp') },
      { title: 'Female', dataIndex: 'female_count', key: 'female', width: 80, sorter: true, sortOrder: sortOrderFor('female') },
    ]
  }, [deptSort])

  // ── Companies — server-side pagination / search / sort ──
  // Companies are independent of the analytics cohort filters, so this
  // effect does NOT depend on appliedFilters. Pagination is driven by the
  // standalone <Pagination> control; the table's onChange only carries sort.
  useEffect(() => {
    const t = setTimeout(() => {
      setCompaniesSearchQuery(prev => {
        const next = companiesSearch.trim()
        if (next !== prev) setCompaniesPage(1)
        return next
      })
    }, 400)
    return () => clearTimeout(t)
  }, [companiesSearch])

  useEffect(() => {
    fetchCorporateCompanies({
      page: companiesPage,
      limit: companiesPageSize,
      search: companiesSearchQuery || undefined,
      // companiesSort.columnKey already matches the API's sortBy tokens
      // (roles / active / applied / placed / rejected).
      sortBy: companiesSort.columnKey || undefined,
      sortOrder: companiesSort.columnKey ? toApiOrder(companiesSort.order) : undefined,
    })
  }, [companiesPage, companiesPageSize, companiesSearchQuery, companiesSort.columnKey, companiesSort.order])

  const handleCompaniesTableChange = (_pagination, _filters, sorter, extra) => {
    if (extra?.action === 'sort') {
      setCompaniesSort({ columnKey: sorter?.order ? sorter.columnKey : undefined, order: sorter?.order || undefined })
      setCompaniesPage(1)
    }
  }

  // ── Top Candidates Shortlist — server-side pagination / search / sort ──
  useEffect(() => {
    const t = setTimeout(() => {
      setShortlistSearchQuery(prev => {
        const next = searchText.trim()
        if (next !== prev) setShortlistPage(1)
        return next
      })
    }, 400)
    return () => clearTimeout(t)
  }, [searchText])

  useEffect(() => {
    fetchCorporateShortlistTable({
      ...buildPayload(appliedFilters),
      page: shortlistPage,
      limit: shortlistPageSize,
      search: shortlistSearchQuery || undefined,
      sortBy: shortlistSort.field,        // column key == API sortBy token
      sortOrder: toApiOrder(shortlistSort.order),
    })
  }, [appliedFilters, shortlistPage, shortlistPageSize, shortlistSearchQuery, shortlistSort.field, shortlistSort.order])

  const handleShortlistTableChange = (pagination, _filters, sorter, extra) => {
    if (extra?.action === 'sort') {
      setShortlistSort(sorter?.order ? { field: sorter.columnKey, order: sorter.order } : { field: undefined, order: undefined })
      setShortlistPage(1)
    } else if (extra?.action === 'paginate') {
      if (pagination.pageSize !== shortlistPageSize) {
        setShortlistPageSize(pagination.pageSize)
        setShortlistPage(1)
      } else {
        setShortlistPage(pagination.current)
      }
    }
  }

  const shortlistColumns = useMemo(() => {
    const orderFor = key => (shortlistSort.field === key ? shortlistSort.order : null)
    return [
      { title: 'Name',       dataIndex: 'full_name',            key: 'name',    onHeaderCell: noWrapHeader, render: v => <span style={{ fontWeight: 500 }}>{v}</span> },
      { title: 'Gender',     dataIndex: 'gender',               key: 'gender',  onHeaderCell: noWrapHeader, render: v => v ? v.charAt(0) + v.slice(1).toLowerCase() : '—' },
      { title: 'Age',        dataIndex: 'age',                  key: 'age',     onHeaderCell: noWrapHeader },
      { title: 'State',      dataIndex: 'perm_state',           key: 'state',   onHeaderCell: noWrapHeader },
      { title: 'Department', dataIndex: 'department',           key: 'dept',    onHeaderCell: noWrapHeader },
      { title: 'College',    dataIndex: 'institute_campus_name',key: 'college', onHeaderCell: noWrapHeader, ellipsis: true },
      { title: '10th',       dataIndex: 'tenth_marks',          key: 'tenth',   onHeaderCell: noWrapHeader, render: v => v != null ? `${parseFloat(v).toFixed(1)}%` : '—' },
      { title: '12th',       dataIndex: 'twelfth_marks',        key: 'twelfth', onHeaderCell: noWrapHeader, render: v => v != null ? `${parseFloat(v).toFixed(1)}%` : '—' },
      { title: 'Diploma',    dataIndex: 'diploma_marks',        key: 'diploma', onHeaderCell: noWrapHeader, render: v => v != null ? `${parseFloat(v).toFixed(1)}%` : '—', sorter: true, sortOrder: orderFor('diploma') },
      { title: 'UG',         dataIndex: 'ug_marks',             key: 'ug',      onHeaderCell: noWrapHeader, render: v => v != null ? `${parseFloat(v).toFixed(1)}%` : '—' },
      { title: 'PG',         dataIndex: 'pg_marks',             key: 'pg',      onHeaderCell: noWrapHeader, render: v => v != null ? `${parseFloat(v).toFixed(1)}%` : '—' },
      { title: 'Arrears',    dataIndex: 'no_of_arrears',        key: 'arrears', onHeaderCell: noWrapHeader, render: v => <Tag color={v === 0 ? 'green' : v <= 2 ? 'orange' : 'red'}>{v ?? '—'}</Tag> },
      {
        title: 'Score', dataIndex: 'placement_score', key: 'score', onHeaderCell: noWrapHeader,
        render: v => v != null ? <Tag color="blue">{v}/100</Tag> : <span style={{ color: '#bbb' }}>—</span>,
        sorter: true, sortOrder: orderFor('score'),
      },
    ]
  }, [shortlistSort])

  // ── Campus Quality Ranking — server-side pagination / search / sort ──
  useEffect(() => {
    const t = setTimeout(() => {
      setCampusSearchQuery(prev => {
        const next = campusQualitySearch.trim()
        if (next !== prev) setCampusPage(1)
        return next
      })
    }, 400)
    return () => clearTimeout(t)
  }, [campusQualitySearch])

  useEffect(() => {
    fetchCorporateCampusQualityTable({
      ...buildPayload(appliedFilters),
      page: campusPage,
      limit: campusPageSize,
      search: campusSearchQuery || undefined,
      sortBy: CAMPUS_SORT_FIELD[campusSort.field] || undefined,
      sortOrder: toApiOrder(campusSort.order),
    })
  }, [appliedFilters, campusPage, campusPageSize, campusSearchQuery, campusSort.field, campusSort.order])

  const handleCampusTableChange = (pagination, _filters, sorter, extra) => {
    if (extra?.action === 'sort') {
      setCampusSort(sorter?.order ? { field: sorter.columnKey, order: sorter.order } : { field: undefined, order: undefined })
      setCampusPage(1)
    } else if (extra?.action === 'paginate') {
      if (pagination.pageSize !== campusPageSize) {
        setCampusPageSize(pagination.pageSize)
        setCampusPage(1)
      } else {
        setCampusPage(pagination.current)
      }
    }
  }

  // Changing the analytics cohort resets every cohort-driven table to page 1
  // (dept, shortlist & campus). Companies aren't cohort-scoped.
  const resetCohortTablePages = () => { setDeptPage(1); setShortlistPage(1); setCampusPage(1) }

  const handleApplyFilter = () => {
    setAppliedFilters(pendingFilters)
    resetCohortTablePages()
    setFilterOpen(false)
    fetchCorporateData(buildPayload(pendingFilters))
  }

  const handleReset = () => {
    setAppliedFilters(EMPTY_PENDING)
    setPendingFilters(EMPTY_PENDING)
    resetCohortTablePages()
    setFilterOpen(false)
    fetchCorporateData({})
  }

  const handleRemoveChip = (key, val) => {
    const next = { ...appliedFilters }
    if (key === 'company' || key === 'minMarks' || key === 'maxArrears') next[key] = undefined
    else next[key] = toArr(next[key]).filter(v => v !== val)
    setAppliedFilters(next)
    setPendingFilters(next)
    resetCohortTablePages()
    fetchCorporateData(buildPayload(next))
  }

  const toggleCheck = (key, val) => {
    setPendingFilters(prev => {
      const arr = toArr(prev[key])
      return { ...prev, [key]: arr.includes(val) ? arr.filter(v => v !== val) : [...arr, val] }
    })
  }

  const catCount = key => {
    if (key === 'company') return pendingFilters.company ? 1 : 0
    if (key === 'minMarks') return pendingFilters.minMarks > 0 ? 1 : 0
    if (key === 'maxArrears') return pendingFilters.maxArrears != null ? 1 : 0
    return toArr(pendingFilters[key]).length
  }

  const getCatOptions = key => {
    const opts = {
      degree: (filterDegrees     || []).map(v => ({ value: v, label: v })),
      dept:   (filterDepartments || []).map(v => ({ value: v, label: v })),
      state:  (filterStates      || []).map(v => ({ value: v, label: v })),
      gender: GENDER_OPTIONS,
    }[key] || []
    if (!catSearch.trim()) return opts
    const q = catSearch.toLowerCase()
    return opts.filter(o => o.label.toLowerCase().includes(q))
  }

  const handlePlacementKPIClick = useCallback((sortKey) => {
    setCompaniesSort(prev => {
      const alreadyActive = prev.columnKey === sortKey && prev.order === 'descend'
      return alreadyActive ? { columnKey: undefined, order: undefined } : { columnKey: sortKey, order: 'descend' }
    })
    setCompaniesPage(1) // KPI-driven sort fetches a fresh first page server-side
    companiesSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [])

  const openRoleCandidatesDrawer = useCallback(async (role, type) => {
    setRoleCandidatesDrawer({ open: true, roleTitle: role.title || '—', type, data: [], loading: true })
    try {
      const res = await adminRequest.get(`/meta-dashboard/job-roles/${role.id}/candidates?type=${type}`)
      setRoleCandidatesDrawer(prev => ({ ...prev, data: res?.data?.data || [], loading: false }))
    } catch {
      setRoleCandidatesDrawer(prev => ({ ...prev, loading: false }))
    }
  }, [])

  const openPotentialDrawer = useCallback(async (role) => {
    setPotentialDrawer({ open: true, roleTitle: role.title || '—', role: null, students: [], campuses: [], total_eligible: 0, loading: true })
    try {
      const res = await adminRequest.get(`/meta-dashboard/job-roles/${role.id}/potential`)
      const d = res?.data?.data || {}
      setPotentialDrawer(prev => ({ ...prev, role: d.role || null, students: d.students || [], campuses: d.campuses || [], total_eligible: d.total_eligible || 0, loading: false }))
    } catch {
      setPotentialDrawer(prev => ({ ...prev, loading: false }))
    }
  }, [])

  const openStudentsDrawer = useCallback(async (company, type) => {
    const totalCount = type === 'placed' ? company.placed_count : type === 'rejected' ? company.rejected_count : company.applied_count
    setStudentsDrawer({ open: true, companyName: company.company_name, type, totalCount: totalCount || 0, data: [], loading: true })
    try {
      const res = await adminRequest.get(`/meta-dashboard/corporate/${company.id}/students?type=${type}`)
      setStudentsDrawer(prev => ({ ...prev, data: res?.data?.data || [], loading: false }))
    } catch {
      setStudentsDrawer(prev => ({ ...prev, loading: false }))
    }
  }, [])

  const openJobRolesDrawer = useCallback(async (company) => {
    setJobRolesDrawer({ open: true, companyName: company.company_name, data: [], loading: true })
    try {
      const res = await adminRequest.get(`/meta-dashboard/corporate/${company.id}/job-roles`)
      setJobRolesDrawer(prev => ({ ...prev, data: res?.data?.data || [], loading: false }))
    } catch {
      setJobRolesDrawer(prev => ({ ...prev, loading: false }))
    }
  }, [])

  const openCompanyDrawer = useCallback(async (company) => {
    setCompanyDrawer({ open: true, company, jobRoles: [], loading: true })
    try {
      const res = await adminRequest.get(`/meta-dashboard/corporate/${company.id}/job-roles`)
      setCompanyDrawer(prev => ({ ...prev, jobRoles: res?.data?.data || [], loading: false }))
    } catch {
      setCompanyDrawer(prev => ({ ...prev, loading: false }))
    }
  }, [])

  // KPI counts (e.g. High Performers, Zero Arrears) are computed without the
  // shortlist's default marks-floor / arrears-ceiling, so the count won't
  // match unless we explicitly disable the *other* default when applying the
  // KPI's own filter. `filterClear` on the KPI definition lists the
  // dimensions to neutralise — `minMarks: 0` and `maxArrears: NO_ARREARS_CEILING` flow
  // through to the SQL as no-op conditions.
  const handleKPIClick = useCallback((filterKey, filterVal, filterClear) => {
    setAppliedFilters(prev => {
      const next = { ...prev }
      const toggleOff = next[filterKey] === filterVal
      if (toggleOff) {
        next[filterKey] = undefined
        if (filterClear) Object.keys(filterClear).forEach(k => { next[k] = undefined })
      } else {
        next[filterKey] = filterVal
        if (filterClear) Object.assign(next, filterClear)
      }
      fetchCorporateData(buildPayload(next))
      return next
    })
    // cohort changed → reset cohort-driven tables to page 1
    setDeptPage(1); setShortlistPage(1); setCampusPage(1)
  }, [fetchCorporateData])

  const activeChips = useMemo(() => {
    const chips = []
    const f = appliedFilters
    if (f.company) {
      const co = (allCompanies.length ? allCompanies : corporateCompanies?.data || []).find(c => c.id === f.company)
      chips.push({ key: 'company', val: f.company, label: `Company: ${co?.company_name || f.company}` })
    }
    ;['degree', 'dept', 'state', 'gender'].forEach(key => {
      toArr(f[key]).forEach(val => {
        const catLabel = FILTER_CATS.find(c => c.key === key)?.label || key
        const displayLabel = key === 'gender' ? (GENDER_OPTIONS.find(o => o.value === val)?.label || val) : val
        chips.push({ key, val, label: `${catLabel}: ${displayLabel}` })
      })
    })
    if (f.minMarks > 0) chips.push({ key: 'minMarks', val: f.minMarks, label: `Min Marks: ${f.minMarks}%` })
    if (f.maxArrears != null && f.maxArrears < NO_ARREARS_CEILING) chips.push({ key: 'maxArrears', val: f.maxArrears, label: `Max Arrears: ${f.maxArrears}` })
    return chips
  }, [appliedFilters, allCompanies, corporateCompanies])

  const totalActiveFilters = activeChips.length

  const ps = corporatePlacementSummary || {}
  const placementKPIs = [
    { icon: <BankOutlined />,        label: 'Companies',        value: ps.total_companies ?? '—', color: '#3249D7', accent: '#3249D7', sortKey: null,      tooltip: 'View companies', scrollToCompanies: true },
    { icon: <BookOutlined />,        label: 'Job Roles Floated',value: ps.total_job_roles  ?? '—', color: '#7c3aed', accent: '#7c3aed', sortKey: 'roles',   tooltip: 'Sort companies by job roles' },
    { icon: <FileSearchOutlined />,  label: 'Total Applied',    value: ps.total_applied    ?? '—', color: '#0891b2', accent: '#0891b2', sortKey: 'applied',  tooltip: 'Sort companies by applied count' },
    { icon: <CheckCircleOutlined />, label: 'Total Placed',     value: ps.total_placed     ?? '—', color: '#059669', accent: '#059669', sortKey: 'placed',   tooltip: 'Sort companies by placed count' },
    { icon: <CloseCircleOutlined />, label: 'Not Selected',     value: ps.total_rejected   ?? '—', color: '#e65100', accent: '#e65100', sortKey: 'rejected', tooltip: 'Sort companies by not-selected count' },
  ]
  const roleBreakdownKPIs = [
    { icon: <BookOutlined />,        label: 'Total Floated', value: ps.total_job_roles  ?? '—', color: '#7c3aed', accent: '#7c3aed', sortKey: 'roles',   tooltip: 'Sort companies by job roles' },
    { icon: <CheckCircleOutlined />, label: 'Active Roles',  value: ps.active_roles     ?? '—', color: '#059669', accent: '#059669', sortKey: 'active',  tooltip: 'Sort companies by active roles' },
    { icon: <CloseCircleOutlined />, label: 'Closed',        value: ps.closed_roles     ?? '—', color: '#f5222d', accent: '#f5222d', scrollToCompanies: true, tooltip: 'View companies' },
    { icon: <InboxOutlined />,       label: 'Archived',      value: ps.archived_roles   ?? '—', color: '#faad14', accent: '#faad14', scrollToCompanies: true, tooltip: 'View companies' },
    { icon: <FileSearchOutlined />,  label: 'Draft',         value: ps.draft_roles      ?? '—', color: '#8c8c8c', accent: '#8c8c8c', scrollToCompanies: true, tooltip: 'View companies' },
    { icon: <BankOutlined />,        label: 'By Corporate',  value: ps.corporate_roles  ?? '—', color: '#3249D7', accent: '#3249D7', scrollToCompanies: true, tooltip: 'View companies' },
    { icon: <BookOutlined />,        label: 'By Institute',  value: ps.institute_roles  ?? '—', color: '#d97706', accent: '#d97706', scrollToCompanies: true, tooltip: 'View companies' },
  ]

  const companiesColumns = useMemo(() => [
    {
      title: 'Company', dataIndex: 'company_name', key: 'company', ellipsis: true, width: 220,
      render: (v, record) => (
        <AntTooltip title="View company analytics">
          <strong
            style={{ color: '#3249D7', cursor: 'pointer' }}
            onClick={e => { e.stopPropagation(); openCompanyDrawer(record) }}
          >
            {v}
          </strong>
        </AntTooltip>
      ),
    },
    { title: 'State',        dataIndex: 'state',         key: 'state',    width: 110 },
    {
      title: 'Job Roles', dataIndex: 'job_roles_count', key: 'roles', width: 110,
      sorter: true,
      sortOrder: companiesSort.columnKey === 'roles' ? companiesSort.order : null,
      render: (v, record) => (
        <AntTooltip title="View job roles">
          <Tag color="blue" style={{ cursor: 'pointer' }} onClick={e => { e.stopPropagation(); openJobRolesDrawer(record) }}>{v}</Tag>
        </AntTooltip>
      ),
    },
    { title: 'Active',       dataIndex: 'active_roles_count', key: 'active',   width: 70,  sorter: true, sortOrder: companiesSort.columnKey === 'active' ? companiesSort.order : null, render: v => v > 0 ? <Tag color="green" style={{ fontSize: 11 }}>{v}</Tag> : <span style={{ color: '#bbb' }}>—</span> },
    {
      title: 'Applied', dataIndex: 'applied_count', key: 'applied', width: 80,
      sorter: true,
      sortOrder: companiesSort.columnKey === 'applied' ? companiesSort.order : null,
      render: (v, record) => v > 0
        ? <AntTooltip title="View applied students"><Tag color="blue" style={{ cursor: 'pointer' }} onClick={e => { e.stopPropagation(); openStudentsDrawer(record, 'applied') }}>{v}</Tag></AntTooltip>
        : <Tag color="blue">{v}</Tag>,
    },
    {
      title: 'Placed', dataIndex: 'placed_count', key: 'placed', width: 80,
      sorter: true,
      sortOrder: companiesSort.columnKey === 'placed' ? companiesSort.order : null,
      render: (v, record) => v > 0
        ? <AntTooltip title="View placed students"><Tag color="green" style={{ cursor: 'pointer' }} onClick={e => { e.stopPropagation(); openStudentsDrawer(record, 'placed') }}>{v}</Tag></AntTooltip>
        : <Tag color="green">{v}</Tag>,
    },
    {
      title: 'Not Selected', dataIndex: 'rejected_count', key: 'rejected', width: 130,
      sorter: true,
      sortOrder: companiesSort.columnKey === 'rejected' ? companiesSort.order : null,
      render: (v, record) => v > 0
        ? <AntTooltip title="View not-selected students"><Tag color="orange" style={{ cursor: 'pointer' }} onClick={e => { e.stopPropagation(); openStudentsDrawer(record, 'rejected') }}>{v}</Tag></AntTooltip>
        : <Tag color="default">{v}</Tag>,
    },
  ], [companiesSort])

  // Role Applied Candidate columns. Marks come from education_profile rows
  // keyed by level; any missing level (or empty field) renders an em-dash.
  const roleAppliedColumns = useMemo(() => {
    const dash = v => (v === null || v === undefined || v === '' ? '—' : v)
    // marks arrive as raw numeric strings ("89.000000…"); show a clean number.
    const num = v => {
      if (v === null || v === undefined || v === '') return '—'
      const n = Number(v)
      return Number.isFinite(n) ? Math.round(n * 100) / 100 : v
    }
    return [
      { title: 'Corporate Name', dataIndex: 'corporate_name', key: 'corporate_name', width: 180, ellipsis: true, render: dash },
      { title: 'Student Name',   dataIndex: 'full_name',      key: 'full_name',      width: 180, ellipsis: true, render: dash },
      { title: 'Primary Email',  dataIndex: 'primary_email',  key: 'primary_email',  width: 220, ellipsis: true, render: dash },
      { title: 'Gender',         dataIndex: 'gender',         key: 'gender',         width: 90,  render: dash },
      { title: 'Age',            dataIndex: 'age',            key: 'age',            width: 70,  render: dash },
      { title: 'State',          dataIndex: 'state',          key: 'state',          width: 130, ellipsis: true, render: dash },
      { title: 'Degree',         dataIndex: 'degree',         key: 'degree',         width: 140, ellipsis: true, render: dash },
      { title: 'Department',     dataIndex: 'department',     key: 'department',     width: 160, ellipsis: true, render: dash },
      { title: 'College Name',   dataIndex: 'college_name',   key: 'college_name',   width: 200, ellipsis: true, render: dash },
      { title: '10th',           dataIndex: 'tenth_marks',    key: 'tenth_marks',    width: 80,  render: num },
      { title: '12th',           dataIndex: 'twelfth_marks',  key: 'twelfth_marks',  width: 80,  render: num },
      { title: 'Diploma',        dataIndex: 'diploma_marks',  key: 'diploma_marks',  width: 90,  render: num },
      { title: 'UG',             dataIndex: 'ug_marks',       key: 'ug_marks',       width: 80,  render: num },
      { title: 'PG',             dataIndex: 'pg_marks',       key: 'pg_marks',       width: 80,  render: num },
      { title: 'Arrears',        dataIndex: 'arrears',        key: 'arrears',        width: 90,  render: num },
    ]
  }, [])

  const s = corporateSummary || {}
  // Top KPI strip pulls from the company-unfiltered summary so it keeps
  // platform context even when the analytics charts below are scoped to
  // one company. Falls back to `s` if the global slice isn't loaded yet.
  const gs = (corporateGlobalSummary && Object.keys(corporateGlobalSummary).length) ? corporateGlobalSummary : s

  const kpis = [
    { icon: <TeamOutlined />, label: 'Total Candidates', value: gs.total_candidates ?? '—', bg: '#eef0fb', color: '#3249D7', scrollToShortlist: true, filterClear: { minMarks: 0, maxArrears: NO_ARREARS_CEILING } },
    { icon: <BarChartOutlined />, label: 'Avg Diploma Marks', value: gs.avg_diploma_marks != null ? `${parseFloat(gs.avg_diploma_marks).toFixed(1)}%` : '—', bg: '#e8f5e9', color: '#2e7d32' },
    { icon: <BarChartOutlined />, label: 'Avg UG Marks', value: gs.avg_ug_marks != null ? `${parseFloat(gs.avg_ug_marks).toFixed(1)}%` : '—', bg: '#e0f7fa', color: '#0891b2' },
    { icon: <BarChartOutlined />, label: 'Avg PG Marks', value: gs.avg_pg_marks != null ? `${parseFloat(gs.avg_pg_marks).toFixed(1)}%` : '—', bg: '#e8f5e9', color: '#059669' },
    { icon: <TrophyOutlined />, label: 'Zero Arrears', value: gs.zero_arrears != null ? `${gs.zero_arrears} (${gs.zero_arrears_pct}%)` : '—', bg: '#fff3e0', color: '#e65100', filterKey: 'maxArrears', filterVal: 0, filterClear: { minMarks: 0 } },

    { icon: <TrophyOutlined />, label: 'High Performers', value: gs.high_performers ?? '—', bg: '#e3f2fd', color: '#1565c0', filterKey: 'minMarks', filterVal: 80, filterClear: { maxArrears: NO_ARREARS_CEILING } },
  ]

  const deptChartData = useMemo(() => {
    return (corporateDepartments || [])
      .map(d => ({
        dept: (d.department || '').length > 14 ? d.department.substring(0, 12) + '…' : (d.department || ''),
        Candidates: d.candidate_count || 0,
        'Avg Marks': parseFloat(d.avg_marks) || 0,
      }))
      .sort((a, b) => b.Candidates - a.Candidates)
      .slice(0, 10)
  }, [corporateDepartments])

  const marksBandsData = useMemo(() => {
    const bands = [
      { name: '<50%', count: 0 },
      { name: '50–60%', count: 0 },
      { name: '60–70%', count: 0 },
      { name: '70–80%', count: 0 },
      { name: '80%+', count: 0 },
    ]
    ;(corporateShortlist || []).forEach(st => {
      const m = parseFloat(st.diploma_marks) || 0
      if (m < 50) bands[0].count++
      else if (m < 60) bands[1].count++
      else if (m < 70) bands[2].count++
      else if (m < 80) bands[3].count++
      else bands[4].count++
    })
    return bands
  }, [corporateShortlist])

  const arrearsData = useMemo(() => {
    const zero = parseInt(s.zero_arrears) || 0
    const oneTwo = parseInt(s.one_two_arrears) || 0
    const threePlus = parseInt(s.three_plus_arrears) || 0
    return [
      { name: 'Zero', count: zero, fill: '#52c41a' },
      { name: '1–2', count: oneTwo, fill: '#faad14' },
      { name: '3+', count: threePlus, fill: '#f5222d' },
    ]
  }, [corporateSummary])

  const genderData = useMemo(() => {
    const female = parseInt(s.female_count) || 0
    const other = parseInt(s.other_count) || 0
    const total = parseInt(s.total_candidates) || 0
    return [
      { name: 'Male', value: total - female - other },
      { name: 'Female', value: female },
      ...(other > 0 ? [{ name: 'Other', value: other }] : []),
    ].filter(d => d.value > 0)
  }, [corporateSummary])

  return (
    <div>
      <SummaryGrid>
        {kpis.map((k, i) => {
          const isActive = k.filterKey ? appliedFilters[k.filterKey] === k.filterVal : false
          const tip = k.filterKey ? (isActive ? 'Click to clear filter' : 'Click to filter shortlist') : k.scrollToShortlist ? 'Scroll to candidates list' : null
          const card = (
            <KPICard
              key={i}
              $clickable={!!(k.filterKey || k.scrollToShortlist)}
              $active={isActive}
              $color={k.color}
              onClick={() => {
                if (k.filterKey) {
                  handleKPIClick(k.filterKey, k.filterVal, k.filterClear)
                } else if (k.scrollToShortlist) {
                  // For scroll-only KPIs (Total Candidates), also drop the
                  // shortlist's default marks-floor / arrears-ceiling so the
                  // visible list matches the KPI count.
                  if (k.filterClear) {
                    setAppliedFilters(prev => {
                      const next = { ...prev, ...k.filterClear }
                      fetchCorporateData(buildPayload(next))
                      return next
                    })
                    setDeptPage(1); setShortlistPage(1); setCampusPage(1)
                  }
                  shortlistRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }
              }}
            >
              <IconWrap color={k.color}>{k.icon}</IconWrap>
              <div>
                <KPIValue style={isActive ? { color: k.color } : {}}>{k.value}</KPIValue>
                <KPILabel>{k.label}</KPILabel>
              </div>
            </KPICard>
          )
          return (k.filterKey || k.scrollToShortlist)
            ? <AntTooltip key={i} title={tip}>{card}</AntTooltip>
            : card
        })}
      </SummaryGrid>

      <SectionHeader>
        <SectionTitle>Placement Overview</SectionTitle>
      </SectionHeader>
      <StatCardsRow>
        {placementKPIs.map((k, i) => {
          const isActive = k.sortKey && companiesSort.columnKey === k.sortKey
          const clickable = !!(k.sortKey || k.scrollToCompanies)
          const card = (
            <StatCard
              key={i}
              $accent={isActive ? k.accent : undefined}
              style={{ cursor: clickable ? 'pointer' : 'default', border: isActive ? `1px solid ${k.accent}` : undefined, borderLeft: `4px solid ${k.accent}` }}
              onClick={k.sortKey ? () => handlePlacementKPIClick(k.sortKey) : k.scrollToCompanies ? () => companiesSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }) : undefined}
            >
              <StatIcon $color={k.color}>{k.icon}</StatIcon>
              <div>
                <StatValue>{k.value}</StatValue>
                <StatLabel>{k.label}</StatLabel>
              </div>
            </StatCard>
          )
          return clickable
            ? <AntTooltip key={i} title={isActive ? 'Click to clear sort' : k.tooltip}>{card}</AntTooltip>
            : card
        })}
      </StatCardsRow>

      <SectionHeader style={{ marginTop: 20 }}>
        <SectionTitle>Job Roles Breakdown</SectionTitle>
      </SectionHeader>
      <StatCardsRow>
        {roleBreakdownKPIs.map((k, i) => {
          const rbActive = k.sortKey && companiesSort.columnKey === k.sortKey
          const clickable = !!(k.sortKey || k.scrollToCompanies)
          const card = (
            <StatCard
              key={i}
              $accent={rbActive ? k.accent : undefined}
              style={{ cursor: clickable ? 'pointer' : 'default', border: rbActive ? `1px solid ${k.accent}` : undefined, borderLeft: `4px solid ${k.accent}` }}
              onClick={k.sortKey ? () => handlePlacementKPIClick(k.sortKey) : k.scrollToCompanies ? () => companiesSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }) : undefined}
            >
              <StatIcon $color={k.color}>{k.icon}</StatIcon>
              <div>
                <StatValue>{k.value}</StatValue>
                <StatLabel>{k.label}</StatLabel>
              </div>
            </StatCard>
          )
          return clickable
            ? <AntTooltip key={i} title={rbActive ? 'Click to clear sort' : k.tooltip}>{card}</AntTooltip>
            : card
        })}
      </StatCardsRow>

      <div ref={companiesSectionRef} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 20, marginBottom: 12, gap: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <Select
            showSearch
            allowClear
            optionFilterProp="children"
            placeholder={<span style={{ fontWeight: 700, fontSize: 15, color: '#1a1a2e' }}>Corporate Companies</span>}
            style={{ width: 260 }}
            onChange={(val) => {
              if (val) {
                const co = (allCompanies.length ? allCompanies : corporateCompanies?.data || []).find(c => c.id === val)
                if (co) openCompanyDrawer(co)
              }
            }}
          >
            {(allCompanies.length ? allCompanies : corporateCompanies?.data || []).map(c => (
              <Option key={c.id} value={c.id}>{c.company_name}</Option>
            ))}
          </Select>
          {/* <Input
            prefix={<SearchOutlined style={{ color: '#bbb' }} />}
            placeholder="Search by company…"
            value={companiesSearch}
            onChange={e => setCompaniesSearch(e.target.value)}
            allowClear
            style={{ maxWidth: 280 }}
          /> */}
        </div>
        <Pagination
          current={companiesPage}
          pageSize={companiesPageSize}
          total={corporateCompanies?.total || 0}
          showSizeChanger
          pageSizeOptions={['10', '25', '50', '100']}
          showTotal={t => `${t} companies`}
          onChange={(page, size) => { if (size !== companiesPageSize) { setCompaniesPageSize(size); setCompaniesPage(1) } else { setCompaniesPage(page) } }}
          onShowSizeChange={(_, size) => { setCompaniesPageSize(size); setCompaniesPage(1) }}
          size="small"
        />
      </div>
      <Table
        rowKey="id"
        loading={corporateCompaniesLoading}
        dataSource={corporateCompanies?.data || []}
        columns={companiesColumns}
        size="small"
        scroll={{ x: 700 }}
        pagination={false}
        onChange={handleCompaniesTableChange}
      />

      {/* ── Role Applied Candidate ──────────────────────────────────────── */}
      <div style={{ marginTop: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, gap: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700, fontSize: 16, color: '#1a1a2e' }}>Role Applied Candidate</span>
            <Input
              prefix={<SearchOutlined style={{ color: '#bbb' }} />}
              placeholder="Search student by name / email…"
              value={raSearch}
              onChange={e => setRaSearch(e.target.value)}
              allowClear
              style={{ width: 280 }}
            />
            <Select
              mode="multiple"
              showSearch
              allowClear
              // Server-side search drives the option list — disable Antd's
              // built-in client-side filter so it doesn't drop API results.
              filterOption={false}
              loading={raRolesLoading}
              searchValue={raRoleSearch}
              onSearch={(s) => setRaRoleSearch(s)}
              // Don't clear the search box on close — users were searching,
              // clicking a result to select, and losing their typed text.
              onDropdownVisibleChange={(open) => { if (!open) setRaRoleSearch('') }}
              placeholder="Filter by role (all roles if empty)"
              style={{ width: 380 }}
              value={raRoleIds}
              onChange={(vals) => { setRaRoleIds(vals); setRaPage(1) }}
              maxTagCount="responsive"
              notFoundContent={raRolesLoading ? 'Loading…' : (raRoleSearchQ ? 'No matching roles' : 'No roles')}
              dropdownRender={(menu) => {
                // Select-all only spans the options currently returned by
                // the API (i.e. after the search filter). Counts and the
                // checkbox state both reflect that filtered set.
                const visibleIds       = raRoles.map(r => r.id)
                const selectedSet      = new Set(raRoleIds)
                const visibleSelected  = visibleIds.filter(id => selectedSet.has(id)).length
                const allVisiblePicked = visibleIds.length > 0 && visibleSelected === visibleIds.length
                const someVisible      = visibleSelected > 0 && !allVisiblePicked
                return (
                  <div>
                    <div style={{ padding: '6px 10px', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: 10 }}>
                      <Checkbox
                        indeterminate={someVisible}
                        checked={allVisiblePicked}
                        disabled={raRoles.length === 0}
                        onChange={(e) => {
                          // Add/remove only the visible (search-filtered)
                          // ids; keep any out-of-scope selections.
                          const next = new Set(raRoleIds)
                          if (e.target.checked) visibleIds.forEach(id => next.add(id))
                          else                  visibleIds.forEach(id => next.delete(id))
                          setRaRoleIds(Array.from(next))
                          setRaPage(1)
                        }}
                      >
                        <span style={{ fontSize: 12.5, fontWeight: 500 }}>
                          Select all ({raRoles.length})
                        </span>
                      </Checkbox>
                      {raRoleIds.length > 0 && (
                        <Button
                          type="link"
                          size="small"
                          style={{ marginLeft: 'auto', padding: 0, fontSize: 12 }}
                          onClick={() => { setRaRoleIds([]); setRaPage(1) }}
                        >
                          Clear ({raRoleIds.length})
                        </Button>
                      )}
                    </div>
                    {menu}
                  </div>
                )
              }}
            >
              {raRoles.map(r => (
                // Render the label as Checkbox + text. pointerEvents:none
                // on the Checkbox so the Select's own click handler still
                // toggles selection (the checkbox just reflects state).
                <Option key={r.id} value={r.id} label={`${r.title}${r.corporate_name ? ` — ${r.corporate_name}` : ''}`}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Checkbox
                      checked={raRoleIds.includes(r.id)}
                      style={{ pointerEvents: 'none' }}
                    />
                    <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {r.title}{r.corporate_name ? ` — ${r.corporate_name}` : ''} ({r.applied_count})
                    </span>
                  </div>
                </Option>
              ))}
            </Select>
          </div>
          <Pagination
            current={raPage}
            pageSize={raPageSize}
            total={raData?.total || 0}
            showSizeChanger
            pageSizeOptions={['10', '25', '50', '100']}
            showTotal={t => `${t} candidates`}
            onChange={(page, size) => { if (size !== raPageSize) { setRaPageSize(size); setRaPage(1) } else { setRaPage(page) } }}
            onShowSizeChange={(_, size) => { setRaPageSize(size); setRaPage(1) }}
            size="small"
          />
        </div>
        <Table
          rowKey="id"
          loading={raLoading}
          dataSource={raData?.data || []}
          columns={roleAppliedColumns}
          size="small"
          scroll={{ x: 1600 }}
          pagination={false}
        />
      </div>

      {/* Analytics filter bar + section title — single compact row */}
      <div style={{ display: 'flex', gap: 8, marginBottom: activeChips.length ? 8 : 10, marginTop: 14, alignItems: 'center' }}>
        <Badge count={totalActiveFilters} size="small" offset={[-4, 4]}>
          <Button
            size="small"
            icon={<FilterOutlined />}
            onClick={() => { setPendingFilters(appliedFilters); setCatSearch(''); setActiveCat('company'); setFilterOpen(true) }}
            style={{
              borderRadius: 999,
              border: totalActiveFilters ? '2px solid #3249D7' : undefined,
              color: totalActiveFilters ? '#3249D7' : undefined,
              fontWeight: totalActiveFilters ? 600 : 400,
            }}
          >
            Filters
          </Button>
        </Badge>
        {totalActiveFilters > 0 && (
          <Button size="small" type="link" onClick={handleReset} style={{ color: '#888', fontSize: 12, padding: 0 }}>
            Clear all
          </Button>
        )}
        <span style={{ fontSize: 15, fontWeight: 700, color: '#1a1a2e', marginLeft: 4 }}>Analytics Overview</span>
        <span style={{ fontSize: 11, color: '#aaa', marginLeft: 'auto', whiteSpace: 'nowrap' }}>
          Filters apply to Analytics, Shortlist &amp; Campus Quality below
        </span>
      </div>

      {/* Active filter chips */}
      {activeChips.length > 0 && (
        <FilterChipRow>
          {activeChips.map((chip, i) => (
            <Tag
              key={i}
              closable
              onClose={() => handleRemoveChip(chip.key, chip.val)}
              style={{ borderRadius: 999, fontSize: 12, padding: '2px 10px', background: '#eef0fb', border: '1px solid #c7d0f5', color: '#3249D7' }}
            >
              {chip.label}
            </Tag>
          ))}
        </FilterChipRow>
      )}

      {/* Filter popup */}
      <Modal
        open={filterOpen}
        onCancel={() => setFilterOpen(false)}
        title={<span style={{ fontSize: 14, fontWeight: 600 }}>Filter Analytics</span>}
        width={560}
        footer={
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button onClick={() => { setPendingFilters(EMPTY_PENDING); setCatSearch('') }}>Reset</Button>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button onClick={() => setFilterOpen(false)}>Cancel</Button>
              <Button type="primary" style={{ background: '#3249D7' }} onClick={handleApplyFilter}>
                Apply Filters
              </Button>
            </div>
          </div>
        }
        styles={{ body: { padding: 0 } }}
      >
        <FilterPopup>
          <FilterSidebar>
            {FILTER_CATS.map(cat => (
              <FilterSidebarItem
                key={cat.key}
                $active={activeCat === cat.key}
                onClick={() => { setActiveCat(cat.key); setCatSearch('') }}
              >
                {cat.label}
                {catCount(cat.key) > 0 && (
                  <span style={{ background: '#3249D7', color: '#fff', borderRadius: 999, fontSize: 10, padding: '1px 6px', minWidth: 18, textAlign: 'center' }}>
                    {catCount(cat.key)}
                  </span>
                )}
              </FilterSidebarItem>
            ))}
          </FilterSidebar>
          <FilterPanel>
            {activeCat === 'company' ? (
              <div>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>Filter analytics to students who applied at this company:</div>
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  placeholder="All Companies"
                  style={{ width: '100%' }}
                  value={pendingFilters.company}
                  onChange={v => setPendingFilters(prev => ({ ...prev, company: v }))}
                  getPopupContainer={() => document.body}
                >
                  {((allCompanies.length ? allCompanies : corporateCompanies?.data) || []).map(c => (
                    <Option key={c.id} value={c.id}>{c.company_name}</Option>
                  ))}
                </Select>
              </div>
            ) : activeCat === 'minMarks' ? (
              <div>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>Minimum diploma/avg marks:</div>
                <InputNumber
                  min={0} max={100}
                  value={pendingFilters.minMarks}
                  onChange={v => setPendingFilters(prev => ({ ...prev, minMarks: v || undefined }))}
                  placeholder="e.g. 60"
                  addonAfter="%"
                  style={{ width: 160 }}
                />
              </div>
            ) : activeCat === 'maxArrears' ? (
              <div>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>Maximum active arrears allowed:</div>
                <InputNumber
                  min={0} max={20}
                  value={pendingFilters.maxArrears}
                  onChange={v => setPendingFilters(prev => ({ ...prev, maxArrears: v != null ? v : undefined }))}
                  placeholder="Any"
                  style={{ width: 160 }}
                />
              </div>
            ) : (
              <>
                <Input
                  prefix={<SearchOutlined style={{ color: '#bbb', fontSize: 12 }} />}
                  placeholder={`Search ${FILTER_CATS.find(c => c.key === activeCat)?.label}…`}
                  value={catSearch}
                  onChange={e => setCatSearch(e.target.value)}
                  size="small"
                  allowClear
                  style={{ marginBottom: 6 }}
                />
                {/* Select-all / Clear toolbar — selects every option currently
                    visible after the search filter. */}
                {(() => {
                  const visible       = getCatOptions(activeCat)
                  const selectedSet   = new Set(toArr(pendingFilters[activeCat]))
                  const visibleValues = visible.map(o => o.value)
                  const visibleSelectedCount = visibleValues.filter(v => selectedSet.has(v)).length
                  const allVisibleSelected   = visible.length > 0 && visibleSelectedCount === visible.length
                  const someVisibleSelected  = visibleSelectedCount > 0 && !allVisibleSelected
                  const onToggleAll = () => {
                    setPendingFilters(prev => {
                      const current = new Set(toArr(prev[activeCat]))
                      if (allVisibleSelected) {
                        visibleValues.forEach(v => current.delete(v))
                      } else {
                        visibleValues.forEach(v => current.add(v))
                      }
                      return { ...prev, [activeCat]: Array.from(current) }
                    })
                  }
                  return visible.length === 0 ? null : (
                    <div style={{ padding: '4px 0 6px', borderBottom: '1px solid #f0f0f0', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 10 }}>
                      <Checkbox
                        indeterminate={someVisibleSelected}
                        checked={allVisibleSelected}
                        onChange={onToggleAll}
                      >
                        <span style={{ fontSize: 12.5, fontWeight: 500 }}>
                          Select all ({visible.length})
                        </span>
                      </Checkbox>
                      {visibleSelectedCount > 0 && (
                        <Button
                          type="link"
                          size="small"
                          style={{ marginLeft: 'auto', padding: 0, fontSize: 12 }}
                          onClick={() => setPendingFilters(prev => ({ ...prev, [activeCat]: [] }))}
                        >
                          Clear ({visibleSelectedCount})
                        </Button>
                      )}
                    </div>
                  )
                })()}
                {getCatOptions(activeCat).length === 0 ? (
                  <div style={{ color: '#bbb', fontSize: 12, padding: '20px 0', textAlign: 'center' }}>No options</div>
                ) : (
                  getCatOptions(activeCat).map(opt => (
                    <div key={opt.value} style={{ padding: '4px 0' }}>
                      <Checkbox
                        checked={toArr(pendingFilters[activeCat]).includes(opt.value)}
                        onChange={() => toggleCheck(activeCat, opt.value)}
                      >
                        <span style={{ fontSize: 13 }}>{opt.label}</span>
                      </Checkbox>
                    </div>
                  ))
                )}
              </>
            )}
          </FilterPanel>
        </FilterPopup>
      </Modal>

      {(deptChartData.length > 0 || marksBandsData.length > 0) && (
        <>
          <ChartsGrid>
            <ChartCard>
              <ChartTitle>Department Distribution</ChartTitle>
              <ResponsiveContainer width="100%" height={240}>
                <ComposedChart data={deptChartData} margin={{ top: 4, right: 24, left: -10, bottom: 48 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="dept" tick={{ fontSize: 11 }} angle={-35} textAnchor="end" interval={0} />
                  <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} domain={[0, 100]} unit="%" />
                  <Tooltip />
                  <Legend verticalAlign="top" height={24} wrapperStyle={{ fontSize: 12 }} />
                  <Bar yAxisId="left" dataKey="Candidates" fill="#3249D7" radius={[4, 4, 0, 0]} />
                  <Line yAxisId="right" type="monotone" dataKey="Avg Marks" stroke="#52c41a" strokeWidth={2} dot={{ r: 3 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard>
              <ChartTitle>Gender Distribution</ChartTitle>
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie
                    data={genderData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={4}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {genderData.map((_, i) => (
                      <Cell key={i} fill={GENDER_COLORS[i % GENDER_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard>
              <ChartTitle>Diploma Marks Bands</ChartTitle>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={marksBandsData} margin={{ top: 4, right: 16, left: -10, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="count" name="Students" radius={[4, 4, 0, 0]}>
                    {marksBandsData.map((_, i) => (
                      <Cell key={i} fill={MARKS_COLORS[i % MARKS_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard>
              <ChartTitle>Arrears Distribution</ChartTitle>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={arrearsData} margin={{ top: 4, right: 16, left: -10, bottom: 4 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="count" name="Candidates" radius={[4, 4, 0, 0]}>
                    {arrearsData.map((entry, i) => (
                      <Cell key={i} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </ChartsGrid>
        </>
      )}

      <div ref={shortlistRef} />
      <SectionHeader><SectionTitle>Placement-Ready Candidates</SectionTitle></SectionHeader>
      {(() => {
        // Surface the marks / arrears thresholds that are narrowing the list,
        // so users understand why this count can be lower than the Applied
        // KPI on a company-scoped view.
        const af = appliedFilters || {}
        const showMinMarks   = af.minMarks   !== undefined && af.minMarks   > 0
        const showMaxArrears = af.maxArrears !== undefined && af.maxArrears < NO_ARREARS_CEILING
        const parts = []
        if (showMinMarks)   parts.push(`marks ≥ ${af.minMarks}%`)
        if (showMaxArrears) parts.push(`arrears ≤ ${af.maxArrears}`)
        if (!parts.length) return null
        return (
          <div style={{ fontSize: 12, color: '#888', marginTop: -4, marginBottom: 12 }}>
            Threshold: {parts.join(' · ')}. Adjust via Min Marks / Max Arrears filters above.
          </div>
        )
      })()}
      <div style={{ display: 'flex', gap: 12, marginBottom: 12, alignItems: 'center' }}>
        <Input
          prefix={<SearchOutlined style={{ color: '#bbb' }} />}
          placeholder="Search by keyword"
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          allowClear
          style={{ maxWidth: 380 }}
        />
        {/* {searchText && (
          <span style={{ fontSize: 12, color: '#888' }}>
            {corporateShortlistTable?.total || 0} found
          </span>
        )} */}
      </div>
      <Table
        rowKey={(r, i) => r.id || i}
        loading={corporateShortlistTableLoading}
        dataSource={corporateShortlistTable?.data || []}
        columns={shortlistColumns}
        size="small"
        scroll={{ x: 1100 }}
        onChange={handleShortlistTableChange}
        pagination={{ current: shortlistPage, pageSize: shortlistPageSize, total: corporateShortlistTable?.total || 0, showSizeChanger: true, pageSizeOptions: ['10', '25', '50', '100'], position: ['topRight'], showTotal: t => `${t} candidates` }}
      />

      <SectionHeader><SectionTitle>Campus Quality Ranking</SectionTitle></SectionHeader>
      <div style={{ display: 'flex', gap: 12, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <Input
          prefix={<SearchOutlined style={{ color: '#bbb' }} />}
          placeholder="Search by institute or state…"
          value={campusQualitySearch}
          onChange={e => setCampusQualitySearch(e.target.value)}
          allowClear
          style={{ maxWidth: 360 }}
        />
        {/* {campusQualitySearch && (
          <span style={{ fontSize: 12, color: '#888' }}>
            {corporateCampusQualityTable?.total || 0} found
          </span>
        )} */}
      </div>
      <Table
        rowKey="institute_campus_id"
        dataSource={corporateCampusQualityTable?.data || []}
        loading={corporateCampusQualityTableLoading}
        size="small"
        onChange={handleCampusTableChange}
        pagination={{
          current: campusPage,
          pageSize: campusPageSize,
          total: corporateCampusQualityTable?.total || 0,
          showSizeChanger: true,
          pageSizeOptions: ['10', '25', '50', '100'],
          position: ['topRight'],
          showTotal: t => `${t} campuses`,
        }}
        scroll={{ x: 800 }}
        tableLayout="fixed"
        className="campus-quality-table"
        columns={[
          // rank reflects the absolute position across pages, not the page-local index
          { title: '#', key: 'rank', render: (_, __, i) => (campusPage - 1) * campusPageSize + i + 1, width: 40 },
          { title: 'Institute', dataIndex: 'institute_name', key: 'inst', ellipsis: true, sorter: true, sortOrder: campusSort.field === 'inst' ? campusSort.order : null },
          { title: 'State', dataIndex: 'state', key: 'state', width: 100, sorter: true, sortOrder: campusSort.field === 'state' ? campusSort.order : null },
          { title: 'Students', dataIndex: 'student_count', key: 'students', width: 80, sorter: true, sortOrder: campusSort.field === 'students' ? campusSort.order : null },
          { title: 'Avg Marks', dataIndex: 'avg_marks', key: 'marks', width: 90, render: v => v != null ? `${parseFloat(v).toFixed(1)}%` : '—', sorter: true, sortOrder: campusSort.field === 'marks' ? campusSort.order : null },
          { title: 'Zero Arrears %', dataIndex: 'zero_arrears_pct', key: 'arr', width: 110, render: v => v != null ? `${parseFloat(v).toFixed(1)}%` : '—', sorter: true, sortOrder: campusSort.field === 'arr' ? campusSort.order : null },
          { title: 'Female %', dataIndex: 'female_pct', key: 'fem', width: 85, render: v => v != null ? `${parseFloat(v).toFixed(1)}%` : '—', sorter: true, sortOrder: campusSort.field === 'fem' ? campusSort.order : null },
          { title: 'Placement Score', dataIndex: 'avg_placement_score', key: 'score', width: 120, render: v => v != null ? <strong style={{ color: '#3249D7' }}>{v}/80</strong> : '—', sorter: true, sortOrder: campusSort.field === 'score' ? campusSort.order : null },
        ]}
        components={{
          header: {
            cell: (props) => <th {...props} style={{ ...props.style, background: 'rgba(50,73,215,0.05)', fontSize: 12 }} />,
          },
        }}
      />

      <SectionHeader><SectionTitle>Department Breakdown</SectionTitle></SectionHeader>
      <div style={{ display: 'flex', gap: 12, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <Input
          prefix={<SearchOutlined style={{ color: '#bbb' }} />}
          placeholder="Search by department…"
          value={deptSearch}
          onChange={e => setDeptSearch(e.target.value)}
          allowClear
          style={{ maxWidth: 320 }}
        />
        {/* {deptSearch && (
          <span style={{ fontSize: 12, color: '#888' }}>
            {corporateDepartmentsTable?.total || 0} found
          </span>
        )} */}
      </div>
      <Table
        rowKey="department"
        dataSource={corporateDepartmentsTable?.data || []}
        loading={corporateDepartmentsTableLoading}
        columns={deptColumns}
        size="small"
        scroll={{ x: 700 }}
        tableLayout="fixed"
        onChange={handleDeptTableChange}
        pagination={{
          current: deptPage,
          pageSize: deptPageSize,
          total: corporateDepartmentsTable?.total || 0,
          showSizeChanger: true,
          pageSizeOptions: ['10', '25', '50', '100'],
          position: ['topRight'],
          showTotal: t => `${t} departments`,
        }}
        components={{
          header: {
            cell: (props) => <th {...props} style={{ ...props.style, background: 'rgba(50,73,215,0.05)', fontSize: 12 }} />,
          },
        }}
      />

      <Drawer
        title={`Job Roles — ${jobRolesDrawer.companyName}`}
        placement="right"
        width={520}
        open={jobRolesDrawer.open}
        onClose={() => setJobRolesDrawer(prev => ({ ...prev, open: false }))}
        styles={{ body: { padding: '20px 24px' }, header: { padding: '16px 24px' } }}
      >
        {jobRolesDrawer.loading ? (
          <div style={{ textAlign: 'center', paddingTop: 60 }}><Spin /></div>
        ) : jobRolesDrawer.data.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#aaa', paddingTop: 60 }}>No job roles found</div>
        ) : (
          <List
            dataSource={jobRolesDrawer.data}
            pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '25', '50', '100'], showTotal: t => `${t} total`, size: 'small' }}
            renderItem={role => (
              <List.Item style={{ padding: '12px 0', borderBottom: '2px solid #e8e8e8', display: 'block' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <strong style={{ fontSize: 14, flex: 1, minWidth: 0 }}>{role.title || '—'}</strong>
                  <Badge
                    color={role.status === 1 ? 'green' : role.status === -1 ? 'red' : 'default'}
                    text={<span style={{ fontSize: 11 }}>{role.status === 1 ? 'ACTIVE' : role.status === -1 ? 'CLOSED' : 'DRAFT'}</span>}
                  />
                  {role.created_at && <span style={{ fontSize: 11, color: '#888', whiteSpace: 'nowrap' }}>{new Date(role.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                  {role.employment_type && <Tag style={{ fontSize: 11 }}>{role.employment_type.replace(/_/g, ' ')}</Tag>}
                  {role.job_type && <Tag style={{ fontSize: 11 }}>{role.job_type.replace(/_/g, ' ')}</Tag>}
                  {role.work_style && (
                    <Tag color={role.work_style === 'REMOTE' ? 'geekblue' : role.work_style === 'HYBRID' ? 'purple' : 'default'} style={{ fontSize: 11 }}>
                      {role.work_style}
                    </Tag>
                  )}
                  {role.role_published_by && (
                    <Tag color={role.role_published_by === 'INSTITUTE' ? 'orange' : 'blue'} style={{ fontSize: 11 }}>
                      {role.role_published_by === 'INSTITUTE' ? 'Institute Invite' : 'Corporate Post'}
                    </Tag>
                  )}
                </div>
                {(role.min_compensation || role.max_compensation) && (
                  <div style={{ marginTop: 4 }}>
                    <Tag color="cyan" style={{ fontSize: 11 }}>
                      ₹{role.min_compensation ? Number(role.min_compensation).toLocaleString('en-IN') : '?'}
                      {role.max_compensation ? ` – ₹${Number(role.max_compensation).toLocaleString('en-IN')}` : ''}
                      {role.ctc_period ? ` / ${role.ctc_period.toLowerCase()}` : ''}
                    </Tag>
                  </div>
                )}
                <div style={{ display: 'flex', gap: 16, marginTop: 6, fontSize: 12, color: '#666', alignItems: 'center', flexWrap: 'wrap' }}>
                  <span
                    style={{ cursor: role.applied_count > 0 ? 'pointer' : 'default' }}
                    onClick={() => role.applied_count > 0 && openRoleCandidatesDrawer(role, 'applied')}
                  >Applied: <strong style={{ color: '#0891b2', textDecoration: role.applied_count > 0 ? 'underline' : 'none' }}>{role.applied_count ?? 0}</strong></span>
                  <span
                    style={{ cursor: role.placed_count > 0 ? 'pointer' : 'default' }}
                    onClick={() => role.placed_count > 0 && openRoleCandidatesDrawer(role, 'placed')}
                  >Placed: <strong style={{ color: '#059669', textDecoration: role.placed_count > 0 ? 'underline' : 'none' }}>{role.placed_count ?? 0}</strong></span>
                  {role.min_vacancies && <span>Vacancies: <strong>{role.min_vacancies}{role.max_vacancies && role.max_vacancies !== role.min_vacancies ? `–${role.max_vacancies}` : ''}</strong></span>}
                </div>
              </List.Item>
            )}
          />
        )}
      </Drawer>

      <Drawer
        title={`Find Eligible Students — ${potentialDrawer.roleTitle}`}
        placement="right"
        width={780}
        open={potentialDrawer.open}
        onClose={() => setPotentialDrawer(prev => ({ ...prev, open: false }))}
        styles={{ body: { padding: '20px 24px' }, header: { padding: '16px 24px' } }}
      >
        {potentialDrawer.loading ? (
          <div style={{ textAlign: 'center', paddingTop: 80 }}><Spin tip="Matching students…" size="large" /></div>
        ) : (
          <>
            {/* Eligibility criteria chips */}
            {potentialDrawer.role && (() => {
              const r = potentialDrawer.role
              const chips = []
              if (r.avg_scroe > 0)    chips.push({ label: `Min Diploma ${r.avg_scroe}%`, color: '#3249D7' })
              if (r.tenth_score > 0)  chips.push({ label: `Min 10th ${r.tenth_score}%`, color: '#0891b2' })
              if (r.twelth_score > 0) chips.push({ label: `Min 12th ${r.twelth_score}%`, color: '#0891b2' })
              if (r.ug_score > 0)     chips.push({ label: `Min UG ${r.ug_score}%`, color: '#059669' })
              if (r.pg_score > 0)     chips.push({ label: `Min PG ${r.pg_score}%`, color: '#059669' })
              if (!r.is_arrears_allowed) chips.push({ label: 'Zero Arrears Only', color: '#e65100' })
              else if (r.arrear_count > 0) chips.push({ label: `Max ${r.arrear_count} Arrear(s)`, color: '#faad14' })
              if (r.is_history_arrears && r.history_arrear_count > 0) chips.push({ label: `Max ${r.history_arrear_count} History Arrears`, color: '#d46b08' })
              const years = Array.isArray(r.year_of_passing) ? r.year_of_passing : (r.year_of_passing ? [] : [])
              if (years.length) chips.push({ label: `Batch: ${years.join(', ')}`, color: '#722ed1' })
              return chips.length > 0 ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
                  {chips.map((c, i) => <Tag key={i} color={c.color} style={{ fontSize: 11 }}>{c.label}</Tag>)}
                </div>
              ) : <div style={{ color: '#aaa', fontSize: 12, marginBottom: 10 }}>No specific eligibility criteria — open to all.</div>
            })()}

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
              <div style={{ fontSize: 13 }}>
                <strong style={{ color: '#3249D7' }}>{potentialDrawer.total_eligible}</strong> eligible student{potentialDrawer.total_eligible !== 1 ? 's' : ''} found
                {potentialDrawer.students.filter(s => s.already_applied).length > 0 && (
                  <span style={{ marginLeft: 8, color: '#059669', fontSize: 12 }}>
                    ({potentialDrawer.students.filter(s => s.already_applied).length} already applied)
                  </span>
                )}
              </div>
              {potentialDrawer.students.length > 0 && (
                <AntTooltip title="Copy apply links for all eligible students to clipboard">
                  <Button
                    size="small"
                    icon={<CopyOutlined />}
                    style={{ borderColor: '#3249D7', color: '#3249D7', fontSize: 12 }}
                    onClick={() => copyAllLinks(potentialDrawer.students.filter(s => s.apply_link))}
                  >
                    Copy All Links ({potentialDrawer.students.filter(s => s.apply_link).length})
                  </Button>
                </AntTooltip>
              )}
            </div>

            <Table
              rowKey={(r, i) => r.id || i}
              dataSource={potentialDrawer.students}
              size="small"
              scroll={{ x: 800 }}
              pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '25', '50', '100'], position: ['topRight', 'bottomRight'], showTotal: (t, range) => `${range[0]}-${range[1]} of ${t} students` }}
              rowClassName={r => r.already_applied ? 'ant-table-row-selected' : ''}
              columns={[
                { title: 'Name', dataIndex: 'full_name', key: 'name', ellipsis: true, onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), render: (v, r) => (
                  <span>
                    <strong style={{ fontSize: 12 }}>{v || '—'}</strong>
                    {r.already_applied && <Tag color="green" style={{ fontSize: 10, marginLeft: 4 }}>Applied</Tag>}
                  </span>
                )},
                { title: 'College', dataIndex: 'institute_campus_name', key: 'college', ellipsis: true, width: 160, onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }) },
                { title: 'Degree', dataIndex: 'degree', key: 'degree', width: 90, ellipsis: true, onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }) },
                { title: 'Dept', dataIndex: 'department', key: 'dept', width: 90, ellipsis: true, onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }) },
                { title: 'Batch', dataIndex: 'batch_year', key: 'batch', width: 60, onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), render: v => v || '—' },
                { title: 'Arrears', dataIndex: 'no_of_arrears', key: 'arr', width: 65, onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), render: v => <Tag color={v === 0 ? 'green' : v != null && v <= 2 ? 'orange' : v != null ? 'red' : 'default'} style={{ fontSize: 11 }}>{v ?? '—'}</Tag> },
                {
                  title: 'Skills', key: 'skills', width: 70, onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
                  render: (_, r) => {
                    if (!r.required_skill_count) return <span style={{ color: '#bbb', fontSize: 11 }}>—</span>
                    const color = r.skill_match_count >= r.required_skill_count ? 'green' : r.skill_match_count > 0 ? 'orange' : 'default'
                    return <AntTooltip title={`${r.skill_match_count} of ${r.required_skill_count} required skills matched`}><Tag color={color} style={{ fontSize: 11 }}>{r.skill_match_count}/{r.required_skill_count}</Tag></AntTooltip>
                  },
                  sorter: (a, b) => (a.skill_match_count || 0) - (b.skill_match_count || 0),
                },
                { title: 'Score', dataIndex: 'match_score', key: 'score', width: 65, defaultSortOrder: 'descend', onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }), sorter: (a, b) => (a.match_score || 0) - (b.match_score || 0), render: v => <Tag color="purple" style={{ fontSize: 11 }}>{v}/100</Tag> },
                {
                  title: 'Apply Link', key: 'apply_link', width: 95, fixed: 'right', onHeaderCell: () => ({ style: { whiteSpace: 'nowrap' } }),
                  render: (_, r) => r.apply_link ? (
                    <AntTooltip title={r.already_applied ? 'Already applied — copy link anyway' : 'Copy unique apply link'}>
                      <Button
                        size="small"
                        icon={<LinkOutlined />}
                        style={{
                          fontSize: 11,
                          height: 24,
                          padding: '0 7px',
                          borderColor: r.already_applied ? '#059669' : '#3249D7',
                          color: r.already_applied ? '#059669' : '#3249D7',
                        }}
                        onClick={() => copyLink(r.apply_link, r.full_name)}
                      >
                        Copy
                      </Button>
                    </AntTooltip>
                  ) : <span style={{ color: '#bbb', fontSize: 11 }}>—</span>,
                },
              ]}
            />

            {potentialDrawer.campuses.length > 0 && (
              <>
                <div style={{ fontWeight: 600, fontSize: 13, margin: '16px 0 8px', borderTop: '2px solid #eef0fb', paddingTop: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <ApartmentOutlined style={{ color: '#3249D7' }} /> Top Campuses by Eligible Count
                </div>
                <Table
                  rowKey="campus_id"
                  dataSource={potentialDrawer.campuses}
                  size="small"
                  pagination={false}
                  columns={[
                    { title: '#', key: 'rank', render: (_, __, i) => i + 1, width: 36 },
                    { title: 'Campus', dataIndex: 'campus_name', key: 'campus', ellipsis: true },
                    { title: 'Eligible', dataIndex: 'eligible_count', key: 'elig', width: 70, render: v => <strong style={{ color: '#3249D7' }}>{v}</strong> },
                    { title: 'Applied', dataIndex: 'already_applied', key: 'applied', width: 65, render: v => <Tag color={v > 0 ? 'green' : 'default'} style={{ fontSize: 11 }}>{v}</Tag> },
                    { title: 'Avg Marks', dataIndex: 'avg_marks', key: 'marks', width: 80, render: v => v != null ? `${v}%` : '—' },
                  ]}
                />
              </>
            )}
          </>
        )}
      </Drawer>

      <Drawer
        title={`${roleCandidatesDrawer.type === 'placed' ? 'Placed' : 'Applied'} Students — ${roleCandidatesDrawer.roleTitle}`}
        placement="right"
        width={780}
        open={roleCandidatesDrawer.open}
        onClose={() => setRoleCandidatesDrawer(prev => ({ ...prev, open: false }))}
        styles={{ body: { padding: '20px 24px' }, header: { padding: '16px 24px' } }}
      >
        {roleCandidatesDrawer.loading ? (
          <div style={{ textAlign: 'center', paddingTop: 60 }}><Spin /></div>
        ) : roleCandidatesDrawer.data.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#aaa', paddingTop: 60 }}>No students found</div>
        ) : (
          <>
            <div style={{ marginBottom: 10, fontSize: 12, color: '#888' }}>{roleCandidatesDrawer.data.length} student{roleCandidatesDrawer.data.length !== 1 ? 's' : ''}</div>
            <Table
              rowKey={(_, i) => i}
              dataSource={roleCandidatesDrawer.data}
              size="small"
              scroll={{ x: 700 }}
              pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '25', '50', '100'], showTotal: t => `${t} total` }}
              columns={[
                { title: 'Name', dataIndex: 'full_name', key: 'name', ellipsis: true, width: 150, render: v => <strong style={{ fontSize: 13 }}>{v || '—'}</strong> },
                { title: 'College', dataIndex: 'institute_campus_name', key: 'college', ellipsis: true, width: 160 },
                { title: 'Dept', dataIndex: 'department', key: 'dept', width: 90, ellipsis: true },
                { title: 'Marks', dataIndex: 'diploma_marks', key: 'marks', width: 70, render: v => v != null ? `${parseFloat(v).toFixed(1)}%` : '—' },
                { title: 'Arrears', dataIndex: 'no_of_arrears', key: 'arr', width: 65, render: v => <Tag color={v === 0 ? 'green' : v <= 2 ? 'orange' : 'red'} style={{ fontSize: 11 }}>{v ?? '—'}</Tag> },
                {
                  title: 'Status', dataIndex: 'offer_status', key: 'status', width: 100,
                  render: v => {
                    const color = v === 'JOINED' ? 'green' : v === 'ACCEPTED' ? 'cyan' : v === 'RELEASED' ? 'blue' : v === 'REJECTED' ? 'red' : 'default'
                    return <Tag color={color} style={{ fontSize: 10 }}>{(v || '—').replace(/_/g, ' ')}</Tag>
                  },
                },
              ]}
            />
          </>
        )}
      </Drawer>

      {/* ── Company Analytics Drawer ── */}
      <Drawer
        title={
          companyDrawer.company
            ? <span>{companyDrawer.company.company_name} <span style={{ fontWeight: 400, color: '#888', fontSize: 13 }}>{companyDrawer.company.state ? `· ${companyDrawer.company.state}` : ''}</span></span>
            : 'Company Analytics'
        }
        placement="right"
        width={820}
        open={companyDrawer.open}
        onClose={() => setCompanyDrawer(prev => ({ ...prev, open: false }))}
        styles={{ body: { padding: '20px 24px' }, header: { padding: '16px 24px' } }}
      >
        {companyDrawer.loading ? (
          <div style={{ textAlign: 'center', paddingTop: 80 }}><Spin size="large" /></div>
        ) : companyDrawer.company && (() => {
          const co = companyDrawer.company
          const roles = companyDrawer.jobRoles

          // KPI cards from company row
          const coKPIs = [
            { label: 'Job Roles',    value: co.job_roles_count      ?? 0, color: '#7c3aed', bg: '#f3e8ff' },
            { label: 'Active Roles', value: co.active_roles_count   ?? 0, color: '#059669', bg: '#e8f5e9' },
            { label: 'Archived',     value: co.archived_roles_count ?? 0, color: '#faad14', bg: '#fff7e0' },
            { label: 'Applied',      value: co.applied_count        ?? 0, color: '#0891b2', bg: '#e0f7fa' },
            { label: 'Placed',       value: co.placed_count         ?? 0, color: '#059669', bg: '#e8f5e9' },
            { label: 'Not Selected', value: co.rejected_count       ?? 0, color: '#e65100', bg: '#fff3e0' },
          ]

          // Chart data: top 10 roles by applied
          const roleChartData = (() => {
            const sorted = [...roles]
              .sort((a, b) => (b.applied_count || 0) - (a.applied_count || 0))
              .slice(0, 10)
            const seen = {}
            return sorted.map(r => {
              let label = (r.title || '—').length > 20 ? r.title.substring(0, 18) + '…' : (r.title || '—')
              seen[label] = (seen[label] || 0) + 1
              if (seen[label] > 1) label = `${label} (${seen[label]})`
              return { name: label, Applied: r.applied_count || 0, Placed: r.placed_count || 0 }
            })
          })()

          return (
            <>
              {/* KPI strip */}
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 24 }}>
                {coKPIs.map((k, i) => (
                  <div key={i} style={{
                    background: '#fff',
                    border: '1px solid #e8ecf4',
                    borderLeft: `3px solid ${k.color}`,
                    borderRadius: 6,
                    padding: '12px 16px',
                    minWidth: 120,
                    flex: '1 1 120px',
                  }}>
                    <div style={{ fontSize: 22, fontWeight: 700, color: k.color }}>{k.value.toLocaleString()}</div>
                    <div style={{ fontSize: 11, color: '#666', marginTop: 4 }}>{k.label}</div>
                  </div>
                ))}
              </div>

              {/* Role-wise chart */}
              {roleChartData.length > 0 && (
                <>
                  <div style={{ fontWeight: 600, fontSize: 13, color: '#1a1a2e', marginBottom: 10 }}>Applied vs Placed by Job Role</div>
                  <div style={{ background: '#fafbfc', border: '1px solid #e8ecf4', borderRadius: 6, padding: '16px 16px 12px', marginBottom: 28 }}>
                    <ResponsiveContainer width="100%" height={Math.max(180, roleChartData.length * 34)}>
                      <BarChart
                        data={roleChartData}
                        layout="vertical"
                        margin={{ top: 4, right: 40, left: 16, bottom: 4 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                        <XAxis type="number" tick={{ fontSize: 11 }} />
                        <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={160} />
                        <Tooltip />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        <Bar dataKey="Applied" fill="#0891b2" radius={[0, 4, 4, 0]} />
                        <Bar dataKey="Placed"  fill="#059669" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </>
              )}

              {/* Job Roles table */}
              <div style={{ fontWeight: 600, fontSize: 13, color: '#1a1a2e', marginBottom: 12 }}>All Job Roles</div>
              {roles.length === 0 ? (
                <div style={{ color: '#bbb', fontSize: 13, padding: '20px 0' }}>No job roles found</div>
              ) : (
                <Table
                  rowKey="id"
                  dataSource={roles}
                  size="small"
                  style={{ marginTop: 4 }}
                  pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '25', '50', '100'], position: ['topRight', 'bottomRight'], showTotal: (t, range) => `${range[0]}-${range[1]} of ${t} roles` }}
                  columns={[
                    { title: 'Job Title', dataIndex: 'title', key: 'title', ellipsis: true, render: v => <strong>{v || '—'}</strong> },
                    {
                      title: 'Type', key: 'type', width: 110,
                      render: (_, r) => r.employment_type
                        ? <Tag style={{ fontSize: 11 }}>{r.employment_type.replace(/_/g, ' ')}</Tag>
                        : '—',
                    },
                    {
                      title: 'Work Style', dataIndex: 'work_style', key: 'ws', width: 90,
                      render: v => v ? <Tag color={v === 'REMOTE' ? 'geekblue' : v === 'HYBRID' ? 'purple' : 'default'} style={{ fontSize: 11 }}>{v}</Tag> : '—',
                    },
                    {
                      title: 'Status', dataIndex: 'status', key: 'status', width: 80,
                      render: v => <Tag color={v === 1 ? 'green' : v === -1 ? 'red' : 'default'} style={{ fontSize: 11 }}>{v === 1 ? 'Active' : v === -1 ? 'Closed' : 'Draft'}</Tag>,
                    },
                    {
                      title: 'Applied', dataIndex: 'applied_count', key: 'applied', width: 75,
                      render: (v, r) => v > 0
                        ? <Tag color="blue" style={{ cursor: 'pointer', fontSize: 11 }} onClick={() => openRoleCandidatesDrawer(r, 'applied')}>{v}</Tag>
                        : <span style={{ color: '#bbb' }}>0</span>,
                    },
                    {
                      title: 'Placed', dataIndex: 'placed_count', key: 'placed', width: 70,
                      render: (v, r) => v > 0
                        ? <Tag color="green" style={{ cursor: 'pointer', fontSize: 11 }} onClick={() => openRoleCandidatesDrawer(r, 'placed')}>{v}</Tag>
                        : <span style={{ color: '#bbb' }}>0</span>,
                    },
                    {
                      title: 'CTC', key: 'ctc', width: 110,
                      render: (_, r) => {
                        const fmt = v => `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`
                        const min = Number(r.min_compensation), max = Number(r.max_compensation)
                        const hasMin = Number.isFinite(min) && min > 0
                        const hasMax = Number.isFinite(max) && max > 0
                        if (!hasMin && !hasMax) return <span style={{ color: '#bbb' }}>—</span>
                        if (hasMin && hasMax && min === max) return <Tag color="cyan" style={{ fontSize: 11 }}>{fmt(min)}</Tag>
                        if (hasMin && hasMax) return <Tag color="cyan" style={{ fontSize: 11 }}>{fmt(min)} – {fmt(max)}</Tag>
                        return <Tag color="cyan" style={{ fontSize: 11 }}>{fmt(hasMax ? max : min)}</Tag>
                      },
                    },
                  ]}
                />
              )}
            </>
          )
        })()}
      </Drawer>

      <Drawer
        title={`${studentsDrawer.type === 'placed' ? 'Placed' : studentsDrawer.type === 'rejected' ? 'Not Selected' : 'Applied'} Students — ${studentsDrawer.companyName}`}
        placement="right"
        width={780}
        open={studentsDrawer.open}
        onClose={() => setStudentsDrawer(prev => ({ ...prev, open: false }))}
        styles={{ body: { padding: '20px 24px' }, header: { padding: '16px 24px' } }}
      >
        {studentsDrawer.loading ? (
          <div style={{ textAlign: 'center', paddingTop: 60 }}><Spin /></div>
        ) : studentsDrawer.data.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#aaa', paddingTop: 60 }}>No students found</div>
        ) : (
          <>
            <div style={{ marginBottom: 10, fontSize: 12, color: '#888' }}>
            {studentsDrawer.totalCount > studentsDrawer.data.length
              ? `Showing top ${studentsDrawer.data.length} of ${studentsDrawer.totalCount} students`
              : `${studentsDrawer.data.length} student${studentsDrawer.data.length !== 1 ? 's' : ''}`}
          </div>
            <Table
              rowKey={(_, i) => i}
              dataSource={studentsDrawer.data}
              size="small"
              scroll={{ x: 750 }}
              pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '25', '50', '100'], showTotal: t => `${t} total` }}
              columns={[
                { title: 'Name', dataIndex: 'full_name', key: 'name', ellipsis: true, width: 150, render: v => <strong style={{ fontSize: 13 }}>{v || '—'}</strong> },
                { title: 'College', dataIndex: 'institute_campus_name', key: 'college', ellipsis: true, width: 160 },
                { title: 'Dept', dataIndex: 'department', key: 'dept', width: 90, ellipsis: true },
                { title: 'Marks', dataIndex: 'diploma_marks', key: 'marks', width: 70, render: v => v != null ? `${parseFloat(v).toFixed(1)}%` : '—' },
                { title: 'Arrears', dataIndex: 'no_of_arrears', key: 'arr', width: 65, render: v => <Tag color={v === 0 ? 'green' : v <= 2 ? 'orange' : 'red'} style={{ fontSize: 11 }}>{v ?? '—'}</Tag> },
                { title: 'Role', dataIndex: 'role_title', key: 'role', ellipsis: true, width: 140 },
                {
                  title: 'Status', dataIndex: 'offer_status', key: 'status', width: 90,
                  render: v => {
                    const color = v === 'JOINED' ? 'green' : v === 'ACCEPTED' ? 'cyan' : v === 'RELEASED' ? 'blue' : v === 'REJECTED' ? 'red' : 'default'
                    return <Tag color={color} style={{ fontSize: 10 }}>{(v || '—').replace(/_/g, ' ')}</Tag>
                  },
                },
              ]}
            />
          </>
        )}
      </Drawer>
    </div>
  )
}

export default CorporateTab
